import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import { getAnswerColor } from '../betLabels';

const EFFECT_INTERVAL = 1000;
const EFFECT_DURATION = 600;
const MIN_CHANGE = 2;

export default function BalanceTrack({ segments, total, compact, snapshotKey }) {
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const previous = useRef(null);
  const lastEffect = useRef(-Infinity);
  const timer = useRef(null);
  const [trail, setTrail] = useState(null);
  const signature = JSON.stringify(segments.map(({ index, barPercent }) => [index, barPercent]));

  useEffect(() => {
    const reset = () => {
      previous.current = null;
      lastEffect.current = -Infinity;
      clearTimeout(timer.current);
      setTrail(null);
    };
    document.addEventListener('visibilitychange', reset);
    window.addEventListener('offline', reset);
    window.addEventListener('online', reset);
    return () => {
      clearTimeout(timer.current);
      document.removeEventListener('visibilitychange', reset);
      window.removeEventListener('offline', reset);
      window.removeEventListener('online', reset);
    };
  }, []);

  useEffect(() => {
    const values = JSON.parse(signature);
    const old = previous.current;
    previous.current = { values, snapshotKey, total };
    if (!old || old.snapshotKey !== snapshotKey || total <= 0 || old.total <= 0 ||
        reducedMotion || document.hidden || old.values.length !== values.length) {
      clearTimeout(timer.current);
      setTrail(null);
      lastEffect.current = -Infinity;
      return;
    }

    // Follow every snapshot; only the optional trail is rate limited, never queued.
    let before = 0;
    let after = 0;
    const boundaries = values.slice(0, -1).map(([index, percent], position) => {
      before += old.values[position][1];
      after += percent;
      return { index, before, after };
    }).filter(({ before: from, after: to }) => Math.abs(to - from) >= MIN_CHANGE);
    const now = Date.now();
    if (!boundaries.length || now - lastEffect.current < EFFECT_INTERVAL) return;
    lastEffect.current = now;
    setTrail({ boundaries, snapshotKey, id: now });
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setTrail(null), EFFECT_DURATION);
  }, [signature, snapshotKey, total, reducedMotion]);

  return (
    <Box data-testid="balance-track" sx={{
      position: 'relative', height: compact ? '18px' : '22px', overflow: 'hidden',
      borderRadius: '5px', bgcolor: 'rgba(15,23,42,0.08)', isolation: 'isolate',
      boxShadow: '0 1px 3px rgba(15,23,42,0.18)',
      '&::after': { content: '""', position: 'absolute', inset: 0, pointerEvents: 'none',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.5), inset 0 -2px 0 rgba(0,0,0,0.12)', zIndex: 2 },
    }}>
      <Box sx={{ display: 'flex', height: '100%' }}>
        {segments.map((segment, position) => {
          const colors = segment.color || getAnswerColor(segment.index);
          return <Box key={segment.index} data-testid={`balance-segment-${segment.index}`} sx={{
            width: `${segment.barPercent}%`, flexShrink: 0, minWidth: 0, overflow: 'hidden',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: total > 0 ? `linear-gradient(180deg, ${colors.primary}, ${colors.hover})` : colors.surface,
            boxShadow: position > 0 ? 'inset 1px 0 0 rgba(255,255,255,0.8)' : 'none',
            transition: reducedMotion ? 'none' : 'width 350ms ease-out',
            color: '#fff', fontSize: compact ? '10px' : '11px', fontWeight: 900, whiteSpace: 'nowrap',
          }}>
            {segments.length > 2 && total > 0 && segment.barPercent >= 14
              ? `${segment.shortLabel} ${segment.percent.toFixed(0)}%` : ''}
          </Box>;
        })}
      </Box>
      {!reducedMotion && trail?.snapshotKey === snapshotKey && trail.boundaries.map(({ index, before, after }) => (
        <Box key={`${trail.id}-${index}`} data-testid="balance-trail" aria-hidden="true" sx={{
          position: 'absolute', top: 0, bottom: 0, pointerEvents: 'none',
          left: `${Math.min(before, after)}%`, width: `${Math.abs(after - before)}%`,
          background: `linear-gradient(${after > before ? '90deg' : '270deg'}, rgba(255,255,255,0.05), rgba(255,255,255,${compact ? 0.35 : 0.6}))`,
          borderLeft: after > before ? '1px solid rgba(255,255,255,0.6)' : 0,
          borderRight: after < before ? '1px solid rgba(255,255,255,0.6)' : 0,
          animation: `balanceTrailFade ${compact ? 450 : EFFECT_DURATION}ms ease-out both`,
          '@keyframes balanceTrailFade': { from: { opacity: 1 }, to: { opacity: 0 } },
        }} />
      ))}
    </Box>
  );
}
